import { useCallback, useEffect, useMemo, useReducer } from 'react';

const CHUNK_SIZE = 1024 * 1024 * 5; // 5MB
const THREADS_QUANTITY = 3;

export type Part = {
  partNumber: number;
  signedUrl: string;
};
export type UploadedPart = {
  partNumber: number;
  eTag: string;
};
type State = {
  aborted: boolean;
  error: Error | null;
  uploading: boolean;
  uploadStarted: boolean;
  uploadId: string | null;
  s3Key: string | null;
  file: File | null;
  filename: string | null;
  fileSize: number | null;
  progress: number;
  done: boolean;
  parts: Part[];
  uploadedParts: { [key: number]: UploadedPart };
};

const initialState: State = {
  aborted: false,
  error: null,
  uploading: false,
  uploadStarted: false,
  uploadId: null,
  s3Key: null,
  file: null,
  filename: null,
  fileSize: null,
  progress: 0,
  done: false,
  parts: [],
  uploadedParts: {},
};

type Payload = Partial<State>;
const reducer = (state: State, action: { type: 'SET'; payload: Payload }) => {
  const { type, payload } = action;

  switch (type) {
    case 'SET': {
      return {
        ...state,
        ...payload,
      };
    }
  }
};

export type GetPresignedUrlsArgs = {
  filename: string;
  numParts: number;
};
export type CompleteUploadArgs = {
  uploadId: string;
  s3Filename: string;
  fileSize: number;
  parts: UploadedPart[];
};
export type GetPresignedUrlsResponse = {
  s3Filename: string;
  uploadId: string;
  parts: Part[];
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

type MultipartUploadProps = {
  chunkSize?: number;
  threadsQuantity?: number;
  getPresignedUrls: (
    args: GetPresignedUrlsArgs
  ) => Promise<GetPresignedUrlsResponse>;
  completeUpload: (args: CompleteUploadArgs) => Promise<void>;
};

export default function useMultipartUpload({
  chunkSize = CHUNK_SIZE,
  threadsQuantity = THREADS_QUANTITY,
  getPresignedUrls,
  completeUpload,
}: MultipartUploadProps): {
  upload: (file: File) => void;
  abort: () => void;
  reset: () => void;
  state: {
    error: Error | null;
    uploadId: string | null;
    s3Key: string | null;
    progress: number;
    done: boolean;
    uploading: boolean;
  };
} {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    error,
    uploadId,
    s3Key,
    progress,
    done,
    parts,
    file,
    uploading,
    uploadStarted,
    uploadedParts,
  } = state;

  function set(payload: Payload) {
    dispatch({ type: 'SET', payload });
  }

  function upload(fileToUpload: File) {
    set({ ...initialState, uploading: true });

    const numParts = Math.ceil(fileToUpload.size / chunkSize);

    getPresignedUrls({
      filename: fileToUpload.name,
      numParts,
    })
      .then((res) => {
        set({
          file: fileToUpload,
          filename: fileToUpload.name,
          fileSize: fileToUpload.size,
          parts: res.parts,
          uploadStarted: true,
          s3Key: res.s3Filename,
          uploadId: res.uploadId,
        });
      })
      .catch((error) => {
        set({ error, done: true, uploading: false });
      });
  }

  const uploadChunk = useCallback(
    async (part: Part, chunk: Blob, retry = 0) => {
      const xhr = new XMLHttpRequest();
      const url = part.signedUrl;

      xhr.open('PUT', url, true);
      // Note: Don't set Content-Type header to avoid CORS preflight issues
      // S3 will handle the content type based on the file extension

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round(
            (((part.partNumber - 1) * chunkSize + event.loaded) /
              file.size /
              parts.length) *
              100
          );
          set({ progress: percentCompleted });
        }
      };

      xhr.send(chunk);

      xhr.onload = () => {
        if (xhr.status === 200) {
          const remainingParts = parts.slice(1);
          const eTag = xhr.getResponseHeader('ETag');
          set({
            parts: remainingParts,
            uploadedParts: {
              ...uploadedParts,
              [part.partNumber]: {
                partNumber: part.partNumber,
                eTag: eTag ? eTag.replaceAll('"', '') : '',
              },
            },
          });
        } else {
          console.log(`Failed to upload part #${part.partNumber}`);
          if (retry < 3) {
            console.log(`Retrying upload of part #${part.partNumber}`);
            delay(retry * 1000).then(() => uploadChunk(part, chunk, retry + 1));
          } else {
            set({
              error: new Error(`Failed to upload file.`),
            });
          }
        }
      };

      xhr.onerror = () => {
        console.log(`Failed to upload part #${part.partNumber}`);
        if (retry < 3) {
          console.log(`Retrying upload of part #${part.partNumber}`);
          delay(retry * 2000).then(() => uploadChunk(part, chunk, retry + 1));
        } else {
          xhr.abort();
          abort();
        }
      };
    },
    [file, chunkSize, parts]
  );

  const sendNextPart = useCallback(() => {
    if (!parts.length) return;

    const part = parts[0];

    const sentSize = (part.partNumber - 1) * chunkSize;
    const chunk = file.slice(sentSize, sentSize + chunkSize);

    uploadChunk(part, chunk);
  }, [file, parts]);

  useEffect(
    function handleSendNextPart() {
      if (uploadStarted && !done && !error && parts.length > 0) {
        sendNextPart();
      }
    },
    [uploadStarted, done, error, parts, sendNextPart]
  );

  function abort() {
    set({
      done: true,
      uploading: false,
      aborted: true,
      error: new Error('Upload aborted due to network connection.'),
    });
  }

  function reset() {
    set(initialState);
  }

  useEffect(
    function checkIfAllPartsUploaded() {
      if (done || !file || !uploadStarted) return;

      if (parts.length === 0) {
        const uploadedParts = Object.values(state.uploadedParts);
        const sortedParts = uploadedParts.sort(
          (a, b) => a.partNumber - b.partNumber
        );

        completeUpload({
          uploadId,
          s3Filename: s3Key,
          fileSize: file.size,
          parts: sortedParts,
        })
          .then(() => {
            set({ done: true, uploading: false });
          })
          .catch((error) => {
            set({ error, done: true, uploading: false });
          });
      }
    },
    [parts, done, file]
  );

  const returnValues = useMemo(() => {
    return {
      upload,
      abort,
      reset,
      state: { error, uploadId, s3Key, progress, done, uploading },
    };
  }, [upload, abort, reset, error, uploadId, s3Key, progress, done, uploading]);

  return returnValues;
}
